import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DatePicker, Space } from "antd";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaLock,
  FaLockOpen,
  FaFilter,
  FaCalendarAlt,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import SalaryView from "./SalaryView";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
const { RangePicker } = DatePicker;

const SalaryListChild = () => {
  dayjs.extend(utc);
  const navigate = useNavigate();
  const [{ errorLogApi, user }] = useAtom(gbState);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(true);

  // Month filter with current month as default
  const currentMonth = dayjs().format("MM");
  const currentMonthLabel = dayjs().format("MMMM YYYY");
  const [monthFilter, setMonthFilter] = useState({
    value: currentMonth,
    label: currentMonthLabel,
    year: dayjs().year(),
  });

  const financial_year = user?.current_financial_year;

  const handleDelete = (id) => {
    confirmAlert({
      title: "",
      message: "Are you sure to delete this Salary ?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(id),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = (id) => {
    let userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };
    axios
      .delete(`${process.env.REACT_APP_API_URL}/salary/delete/${id}`, config)
      .then((response) => {
        getDataFromServer();
      })
      .catch((err) => {
        errorLogApi(err, "SalaryListChild", "/salary/delete");
      });
  };

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/salary/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        const oldData = response.data.salaries?.filter(
          (finFilter) => finFilter?.financial_year === financial_year
        );
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data?._id,
            employee_name:
              data?.employee_id?.first_name +
              " " +
              data?.employee_id?.last_name,
            monthly_record_close_status: data?.monthly_record_close_status,
            date: data?.date,
          };
        });

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, [financial_year]);

  const filteredData = useMemo(() => {
    return data.filter((salary) => {
      const matchesSearch = Object.values(salary).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Parse salary date as UTC and keep it in UTC for all comparisons
      const salaryDate = dayjs.utc(salary.date);

      // For date range filtering, also parse input dates as UTC
      const start = startDate
        ? dayjs.utc(startDate.format("YYYY-MM-DD"))
        : null;
      const end = endDate ? dayjs.utc(endDate.format("YYYY-MM-DD")) : null;

      const matchesDateRange =
        (!start ||
          salaryDate.isAfter(start, "day") ||
          salaryDate.isSame(start, "day")) &&
        (!end ||
          salaryDate.isBefore(end, "day") ||
          salaryDate.isSame(end, "day"));

      // Fixed month filter - extract UTC month/year directly from the date string
      const matchesMonth = monthFilter?.value
        ? (() => {
            const selectedMonth = parseInt(monthFilter.value);
            const selectedYear = monthFilter.year;

            // Parse the date and extract UTC components
            const utcDate = new Date(salary.date);
            const salaryMonth = utcDate.getUTCMonth() + 1; // getUTCMonth() returns 0-11
            const salaryYear = utcDate.getUTCFullYear();

            return salaryMonth === selectedMonth && salaryYear === selectedYear;
          })()
        : true;

      const matchesStatus =
        statusFilter === "all" || salary.status === statusFilter;

      return matchesSearch && matchesDateRange && matchesStatus && matchesMonth;
    });
  }, [data, searchTerm, startDate, endDate, statusFilter, monthFilter]);

  const filteredDatav1 = useMemo(() => {
    return data.filter((salary) => {
      const matchesSearch = Object.values(salary).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Force all dates to UTC mode
      const salaryDate = dayjs.utc(salary.date);
      const start = startDate ? dayjs.utc(startDate) : null;
      const end = endDate ? dayjs.utc(endDate) : null;

      const matchesDateRange =
        (!start ||
          salaryDate.isAfter(start, "day") ||
          salaryDate.isSame(start, "day")) &&
        (!end ||
          salaryDate.isBefore(end, "day") ||
          salaryDate.isSame(end, "day"));

      const matchesMonth = monthFilter?.value
        ? (() => {
            const selectedMonth = parseInt(monthFilter.value);
            const selectedYear = monthFilter.year;

            const salaryMonth = salaryDate.month() + 1;
            const salaryYear = salaryDate.year();

            return salaryMonth === selectedMonth && salaryYear === selectedYear;
          })()
        : true;

      const matchesStatus =
        statusFilter === "all" || salary.status === statusFilter;

      return matchesSearch && matchesDateRange && matchesStatus && matchesMonth;
    });
  }, [data, searchTerm, startDate, endDate, statusFilter, monthFilter]);

  // Month options for the filter
  const monthOptions = useMemo(() => {
    const months = [];
    const currentYear = dayjs().year();

    // Generate options for current year and previous year
    for (let year = currentYear - 1; year <= currentYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const monthStr = month.toString().padStart(2, "0");
        const monthLabel = dayjs(`${year}-${monthStr}-01`).format("MMMM YYYY");
        months.push({
          value: monthStr,
          label: monthLabel,
          year: year,
        });
      }
    }

    // Sort by year and month (newest first)
    return months.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.value - a.value;
    });
  }, []);

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStatusFilter("all");
    setSearchTerm("");
    setMonthFilter({
      value: currentMonth,
      label: currentMonthLabel,
      year: dayjs().year(),
    });
  };

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const renderTableCell = (row) => (
    <>
      <TableCell>
        <div className="flex items-center space-x-1">
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={() => {
                toggleModal();
                setCurrentRecord(row);
              }}
            >
              <FaEye className="text-blue-600" />
            </IconButton>
          </Tooltip>
          {user?.is_a_trustee === true && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  if (row.monthly_record_close_status) {
                    toast.error(
                      "Monthly record is already closed for this month."
                    );
                  } else {
                    navigate("/salary/update", { state: row });
                  }
                }}
              >
                <FaEdit className="text-gray-600" />
              </IconButton>
            </Tooltip>
          )}
          {user?.is_a_trustee === true && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => {
                  if (row.monthly_record_close_status) {
                    toast.error(
                      "Cannot be deleted! Monthly record is already closed for this month."
                    );
                  } else {
                    handleDelete(row.id);
                  }
                }}
              >
                <FaTrash className="text-red-600" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </TableCell>
      {!isMobile && (
        <TableCell>
          {row.date ? dayjs(row.date).utc().format("MM/DD/YYYY") : ""}
        </TableCell>
      )}
      <TableCell>{row.employee_name}</TableCell>
      {!isTablet && <TableCell>{row.salary_voucher_number}</TableCell>}
      <TableCell>{row.amount}</TableCell>
      {!isMobile && <TableCell>{row.transfer_source}</TableCell>}
      <TableCell>
        <span
          className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === "1"
              ? "bg-orange-400 text-white"
              : row.status === "2"
              ? "bg-green-400 text-gray-800"
              : "bg-red-400 text-white"
          }`}
        >
          {row.status === "1"
            ? "Pending"
            : row.status === "2"
            ? "Settled"
            : "Declined"}
        </span>
      </TableCell>
      {!isTablet && (
        <TableCell>
          {row.monthly_record_close_status ? (
            <FaLock className="text-red-600" />
          ) : (
            <FaLockOpen className="text-green-600" />
          )}
        </TableCell>
      )}
    </>
  );

  return (
    <div className="px-4 py-6 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between items-start mb-6 sm:flex-row sm:items-center">
        <h2 className="mb-4 text-2xl font-bold text-gray-900 sm:mb-0">
          Salary List
        </h2>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          {user?.is_a_trustee === true && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FaPlus />}
              onClick={() => navigate("/salary/add")}
              className="bg-blue-600 hover:bg-blue-700"
              fullWidth={isMobile}
            >
              Add Salary
            </Button>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search salaries..."
              InputProps={{
                startAdornment: (
                  <FaSearch className="mr-2 text-gray-400" size={16} />
                ),
                className: "bg-white rounded-lg shadow-sm",
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="p-4 mt-3 bg-white rounded-lg border border-gray-100 shadow-sm">
          {/* Month Filter - Top Row */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Filter by Month
            </label>
            <ReactSelect
              value={monthFilter}
              onChange={setMonthFilter}
              options={monthOptions}
              placeholder="Select month..."
              className="w-full"
              classNamePrefix="select"
              isClearable={false}
              isSearchable={true}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Start Date
              </label>
              <div className="w-full">
                <DatePicker
                  className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  placeholder="Select start date"
                  format="MM-DD-YYYY"
                  size="small"
                  style={{ width: "100%" }}
                  allowClear={true}
                />
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                End Date
              </label>
              <div className="w-full">
                <DatePicker
                  className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  placeholder="Select end date"
                  format="MM-DD-YYYY"
                  size="small"
                  style={{ width: "100%" }}
                  allowClear={true}
                />
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Status
              </label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full"
                size="small"
                variant="outlined"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">Pending</MenuItem>
                <MenuItem value="2">Settled</MenuItem>
                <MenuItem value="3">Declined</MenuItem>
              </Select>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              className="text-sm font-medium text-gray-600 border-gray-300 hover:border-gray-400"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <TableContainer component={Paper} className="rounded-lg shadow-lg">
        <Table>
          <TableHead>
            <TableRow className="bg-gray-50">
              <TableCell className="font-semibold">Action</TableCell>
              {!isMobile && (
                <TableCell className="font-semibold">Payment Date</TableCell>
              )}
              <TableCell className="font-semibold">Employee</TableCell>
              {!isTablet && (
                <TableCell className="font-semibold">Receipt Number</TableCell>
              )}
              <TableCell className="font-semibold">Amount</TableCell>
              {!isMobile && (
                <TableCell className="font-semibold">Receive Type</TableCell>
              )}
              <TableCell className="font-semibold">Status</TableCell>
              {!isTablet && (
                <TableCell className="font-semibold">Record Status</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id} className="hover:bg-gray-50">
                {renderTableCell(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex flex-col justify-between items-center p-4 mt-4 bg-white rounded-lg shadow-sm sm:flex-row">
        <div className="mb-2 sm:mb-0">
          <span className="text-sm text-gray-600">Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="p-1 ml-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div className="flex space-x-2">
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/salary/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <SalaryView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default SalaryListChild;
